import { useEffect, useRef, useState } from "react";
import { User, ChevronRight, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export const GuestNameAutocomplete = ({ value = "", onChange, onPick, onOpen, testid = "field-guest_name" }) => {
  const [names, setNames] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { api.guestNames().then(setNames).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setEditing(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const q = String(value || "").trim();
  const suggestions = names
    .filter((n) => n && q && n.toLowerCase().includes(q.toLowerCase()) && n.toLowerCase() !== q.toLowerCase())
    .slice(0, 8);
  // "View profile" is only meaningful when the name matches a guest that already exists.
  const existing = names.find((n) => n && q && n.toLowerCase() === q.toLowerCase());

  const pick = (n) => { onChange(n); setOpen(false); setEditing(false); onPick?.(n); };
  const startEdit = () => { setEditing(true); setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); };
  const clear = () => { onChange(""); setEditing(true); setOpen(true); requestAnimationFrame(() => inputRef.current?.focus()); };

  // Show the guest as a tappable chip (matching linked guests) once a name is set.
  if (q && !editing) {
    return (
      <div ref={boxRef} className="flex items-center gap-2" data-testid={`${testid}-chip`}>
        <button
          type="button"
          onClick={() => (onOpen && existing ? onOpen(existing) : startEdit())}
          className="group flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5 text-left transition-colors duration-200 hover:border-[#E0575B]/40 hover:bg-[#9B2226]/10"
          title={existing ? `Open ${existing}'s profile` : "Edit name"}
          data-testid={existing ? "lead-guest-profile-link" : `${testid}-edit`}
        >
          <User className="h-4 w-4 shrink-0 text-[#E0575B]" />
          <span className="min-w-0 flex-1 truncate text-sm font-600 text-zinc-100">{value}</span>
          {existing ? (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-zinc-500 transition-colors group-hover:text-[#E0575B]">View <ChevronRight className="h-3.5 w-3.5" /></span>
          ) : (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-zinc-500 transition-colors group-hover:text-[#E0575B]">Edit <Pencil className="h-3 w-3" /></span>
          )}
        </button>
        <button
          type="button"
          onClick={clear}
          title="Change lead guest"
          aria-label="Change lead guest"
          className="shrink-0 rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-red-400"
          data-testid={`${testid}-clear`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <Input
        ref={inputRef}
        type="text"
        value={value ?? ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setEditing(true); }}
        onFocus={() => { setOpen(true); setEditing(true); }}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setOpen(false); setEditing(false); inputRef.current?.blur(); } }}
        placeholder="Start typing a name…"
        autoComplete="off"
        className="border-zinc-800 bg-zinc-900 focus-visible:ring-[#E0575B]/70"
        data-testid={testid}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 py-1 shadow-xl" data-testid="guest-name-suggestions">
          {suggestions.map((n) => (
            <button
              key={n}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(n); }}
              className="block w-full px-3 py-1.5 text-left text-sm text-zinc-300 hover:bg-white/5"
              data-testid={`guest-name-sugg-${n}`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
